import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $dashboardNotificationStore, $profileFeatureStore } from '@lib/store';
import { useQuery, useRouteHook } from '@shared/hooks';
import { DateUtils, RoutePaths } from '@shared/utils';
import type { Notification } from '@shared/api/rest/dashboards';
import { Button, Modal, PageContainer, Table, TableCellType, Text } from '@shared/widgets';

export const AlertsPage = () => {
  const query = useQuery();
  const id = query.get('id');
  const { navigate } = useRouteHook();

  const { data: features } = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const [openAlert, setOpenAlert] = useState<Notification | null>(null);

  useEffect(() => {
    if (!features?.settings.viewNotifications) navigate(RoutePaths.dashboard.onNavigate());
    $dashboardNotificationStore.events.getAll();
  }, [features]);

  useEffect(() => {
    if (dashboardNotificationStore.loading || !dashboardNotificationStore.data.length || !id) return;
    const notification = dashboardNotificationStore.data.find((notification) => notification.alertGuid === id) || null;
    setOpenAlert(notification);
  }, [dashboardNotificationStore.loading, dashboardNotificationStore.data, id]);

  const readAll = () => $dashboardNotificationStore.events.markAllAsRead({ allNotifications: true });
  const handleAlertAction = useCallback((alert: Notification) => setOpenAlert(alert), [setOpenAlert]);
  const handleAlertClose = useCallback(() => setOpenAlert(null), [setOpenAlert]);

  const isUnread = useMemo(
    () => dashboardNotificationStore.data.length > 0 && dashboardNotificationStore.data.filter((notification) => !notification.isRead).length > 0,
    [dashboardNotificationStore.data],
  );

  if (!features?.settings.viewNotifications) return null;
  return (
    <PageContainer backText='Back' title='Alerts'>
      <div className='flex justify-between mb-6'>
        <Text size='2xl'>All history</Text>
        <Button color='outlined' onClick={readAll} disabled={dashboardNotificationStore.loading || !isUnread}>
          Mark all as read
        </Button>
      </div>
      <Table
        isScrollable
        isLoading={dashboardNotificationStore.loading}
        columns={['alerts', 'details', 'alert time', '']}
        items={dashboardNotificationStore.data.map((x) => [
          {
            type: TableCellType.Custom,
            value: (
              <div className='flex items-center gap-2'>
                {!x.isRead && <div className='h-2 w-2 my-auto rounded-full bg-green' />}
                <Text>{x.alertTypeDisplayString}</Text>
              </div>
            ),
          },
          { type: TableCellType.Text, value: `${x.subject?.slice(0, 50)}...` },
          { type: TableCellType.Text, value: DateUtils.formatDate(x.createdOn, { withTime: true }) },
          {
            type: TableCellType.Custom,
            value: (
              <Button fullWidth color='outlined' onClick={() => handleAlertAction(x)}>
                View details
              </Button>
            ),
          },
        ])}
      />
      <Modal title='Details' isOpen={!!openAlert} onClose={handleAlertClose} width='332px'>
        <Text className='whitespace-pre-line'>{openAlert?.text}</Text>
        <br />
        {openAlert?.actionUrl && (
          <Button fullWidth color='primary' to={openAlert?.actionUrl} onClick={handleAlertClose}>
            {openAlert?.actionText}
          </Button>
        )}
      </Modal>
    </PageContainer>
  );
};
